/**
 * HarvestHighlightCard -- dashboard widget showing the season's harvest highlights.
 *
 * M81 migration: replaced the legacy URL.createObjectURL + objectUrls array with
 * photo ID resolution only. Sub-components (SingleHarvestDisplay, MultiHarvestDisplay)
 * now render PhotoThumbnail which uses usePhotoSrc for on-demand hydration.
 * M83 (ADR-044): IDB stores bytes as ArrayBuffer; the Blob is reconstructed
 * inside usePhotoSrc at render time.
 *
 * HarvestWithThumb shape changed:
 *   Before: { harvest, thumbUrl: string | null }  (blob URL)
 *   After:  { harvest, thumbPhotoId: string | null }  (photo ID)
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Trophy } from 'lucide-react';

import { db } from '@/services/storage/db';
import { getPhoto, getPhotosForHarvest } from '@/services/photo-service';
import type { HarvestRecord } from '@/types/hunt';
import { CollapsibleWidget } from './collapsible-widget';
import { PhotoThumbnail } from '@/components/photos/PhotoThumbnail';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface HarvestWithThumb {
  harvest: HarvestRecord;
  /** Photo ID to render via PhotoThumbnail, or null when no photo is available. */
  thumbPhotoId: string | null;
}

interface HarvestHighlightData {
  items: HarvestWithThumb[];
}

export interface HarvestHighlightCardProps {
  seasonId: string;
  /**
   * Controls the top accent border on the content card body.
   * - 'ember': shows a 2px ember-500 top border (legacy highlight style)
   * - 'none': no accent border (default)
   */
  accentColor?: 'ember' | 'none';
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function sexLabel(sex: HarvestRecord['sex']): string {
  if (sex === 'buck') return 'Buck';
  if (sex === 'doe') return 'Doe';
  return 'Unknown';
}

function buildCollapsedSummary(items: HarvestWithThumb[]): string {
  if (items.length === 1) {
    const { harvest } = items[0];
    const date = format(parseISO(harvest.date), 'MMM d');
    const pts = harvest.antlerPoints != null ? `${harvest.antlerPoints}-pt ` : '';
    return `${pts}${capitalize(harvest.species)} · ${date}`;
  }
  return `${items.length} harvests this season`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SingleHarvestDisplay({ item, onClick }: { item: HarvestWithThumb; onClick: () => void }) {
  const { harvest, thumbPhotoId } = item;
  const date = format(parseISO(harvest.date), 'MMM d');

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left flex items-center gap-3"
      data-testid="harvest-highlight-single"
    >
      {/* Thumbnail or Trophy icon -- M81: PhotoThumbnail uses usePhotoSrc */}
      <div className="shrink-0 flex items-center justify-center w-16 h-16 rounded-lg overflow-hidden bg-[var(--color-elevated)]">
        {thumbPhotoId ? (
          <PhotoThumbnail
            photoId={thumbPhotoId}
            alt={`${capitalize(harvest.species)} harvest`}
            className="w-full h-full object-cover"
          />
        ) : (
          <Trophy size={40} style={{ color: 'var(--color-ember-400)' }} aria-hidden data-testid="trophy-icon" />
        )}
      </div>

      {/* Details */}
      <div className="min-w-0">
        <p className="font-display text-display-sm text-[var(--color-text-primary)]">
          {harvest.antlerPoints != null ? `${harvest.antlerPoints}-pt ` : ''}
          {capitalize(harvest.species)}
        </p>
        <p className="text-body-sm text-[var(--color-text-secondary)] mt-0.5">
          {sexLabel(harvest.sex)} · {date}
        </p>
      </div>
    </button>
  );
}

function MultiHarvestDisplay({
  items,
  onClick,
}: {
  items: HarvestWithThumb[];
  onClick: () => void;
}) {
  const visibleItems = items.slice(0, 3);
  const overflow = items.length - visibleItems.length;

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left flex flex-col gap-3"
      data-testid="harvest-highlight-multi"
    >
      <p className="text-body-sm font-medium text-[var(--color-text-secondary)]">
        {items.length} harvests this season
      </p>
      <div className="flex items-center gap-2">
        {visibleItems.map(({ harvest, thumbPhotoId }) => (
          <div
            key={harvest.id}
            className="shrink-0 flex items-center justify-center w-12 h-12 rounded-lg overflow-hidden bg-[var(--color-elevated)]"
          >
            {/* M81: PhotoThumbnail uses usePhotoSrc for hydration */}
            {thumbPhotoId ? (
              <PhotoThumbnail
                photoId={thumbPhotoId}
                alt={`${capitalize(harvest.species)} harvest`}
                className="w-full h-full object-cover"
              />
            ) : (
              <Trophy size={28} style={{ color: 'var(--color-ember-400)' }} aria-hidden />
            )}
          </div>
        ))}
        {overflow > 0 && (
          <span className="text-body-sm text-[var(--color-text-muted)]">+{overflow} more</span>
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function HarvestHighlightCard({ seasonId, accentColor = 'none' }: HarvestHighlightCardProps) {
  const navigate = useNavigate();
  const [data, setData] = useState<HarvestHighlightData | null>(null);

  useEffect(() => {
    let active = true;

    async function load() {
      const harvests = await db.getHarvestRecordsBySeason(seasonId);

      if (!active || harvests.length === 0) {
        if (active) setData(null);
        return;
      }

      // M81: resolve photo ID only — no blob URL creation here.
      // PhotoThumbnail / usePhotoSrc handles download and rendering.
      const items: HarvestWithThumb[] = await Promise.all(
        harvests.map(async (harvest) => {
          try {
            let photo = null;
            if (harvest.thumbnailPhotoId) {
              photo = await getPhoto(harvest.thumbnailPhotoId);
            }
            if (!photo) {
              // Fall back to first attached photo when no thumbnail is pinned
              const allPhotos = await getPhotosForHarvest(harvest.id);
              photo = allPhotos[0] ?? null;
            }
            if (!photo) return { harvest, thumbPhotoId: null };
            return { harvest, thumbPhotoId: photo.id };
          } catch {
            return { harvest, thumbPhotoId: null };
          }
        }),
      );

      if (active) {
        setData({ items });
      }
    }

    load().catch((err) => {
      console.error('[HarvestHighlightCard] load:', err);
    });

    return () => {
      active = false;
    };
  }, [seasonId]);

  if (!data || data.items.length === 0) return null;

  const { items } = data;
  const firstHuntId = items[0].harvest.huntId;
  const collapsedText = buildCollapsedSummary(items);

  return (
    <CollapsibleWidget
      id="harvest-highlight"
      title="HARVEST HIGHLIGHT"
      summary={collapsedText}
    >
      <div
        className={[
          'bg-[var(--color-surface)] border-x border-b border-[var(--color-border)] rounded-b-lg rounded-t-none p-4',
          accentColor === 'ember' ? 'border-t-2 border-t-[var(--color-ember-500)]' : '',
        ].filter(Boolean).join(' ')}
        data-testid="harvest-highlight-card"
        data-accent={accentColor}
      >
        {items.length === 1 ? (
          <SingleHarvestDisplay
            item={items[0]}
            onClick={() => navigate(`/hunt/${firstHuntId}`)}
          />
        ) : (
          <MultiHarvestDisplay
            items={items}
            onClick={() => navigate(`/hunt/${firstHuntId}`)}
          />
        )}
      </div>
    </CollapsibleWidget>
  );
}
